import { FC, useEffect, useState } from "react"
import { observer } from "mobx-react"
import { useStore } from "stores/utils/use-store"
import { Breadcrumb, Button, Card, Col, Descriptions, Input, Modal, Row, Space, Table, Tag, Typography } from "antd"
import { Link, useParams } from "react-router-dom"
import { DeliveryBatchStatus, DeliveryItem } from "../store/types"
import { fmtBytes, fmtDuration, fmtHours, replayUrl } from "../utils"
import { BatchValidation } from "./batch-validation"

const BATCH_COLOR: Record<DeliveryBatchStatus, string> = {
  RESERVED: "blue",
  BUILDING: "gold",
  READY: "cyan",
  DELIVERED: "green",
  FAILED: "red",
  CANCELLED: "default",
  DISPUTED: "red",
}

export const DeliveryDetail: FC = observer(() => {
  const { dataStore: { dataDeliveryStore: store } } = useStore()
  const { id: batchId = "" } = useParams<{ id: string }>()
  const [rejectItem, setRejectItem] = useState<{ id: string; recordingId: string } | null>(null)
  const [reason, setReason] = useState("")
  const [itemPage, setItemPage] = useState(1)
  const [itemPageSize, setItemPageSize] = useState(20)

  // A shorter batch would otherwise be opened at the page number the previous
  // one was on, and answer with an empty table.
  useEffect(() => {
    setItemPage(1)
  }, [batchId])

  useEffect(() => {
    store.loadBatch(batchId, { itemsLimit: itemPageSize, itemsOffset: (itemPage - 1) * itemPageSize })
  }, [store, batchId, itemPage, itemPageSize])

  // Only the batch the URL asks for: `batchDetail` still holds the previous one
  // while the new request is in flight, and rendering that would show its items
  // and status — with actions that operate on it — under the new id.
  const detail = store.batchDetail?.batch?.id === batchId ? store.batchDetail : null
  const batch = detail?.batch
  const status = batch?.status

  // Async build: the batch is reserved instantly and built off the request path,
  // so poll until it settles into READY (or a terminal state).
  useEffect(() => {
    if (status !== "RESERVED" && status !== "BUILDING") return undefined
    const t = setInterval(
      () => store.loadBatch(batchId, { itemsLimit: itemPageSize, itemsOffset: (itemPage - 1) * itemPageSize }),
      2500,
    )

    return () => clearInterval(t)
  }, [store, batchId, status, itemPage, itemPageSize])

  if (!batch) {
    return <Card loading={store.isLoading} title="Delivery batch" />
  }

  const items = detail?.items ?? []
  const itemCount = detail?.itemCount ?? 0
  // From the batch, not the loaded page: items are server-paged now.
  const totalSeconds = Number(batch.reservedSeconds || 0)
  const s3 = detail?.s3Verification ?? null
  // Mark-delivered is gated on the delivery artifacts being confirmed in the customer bucket.
  const canDeliver = (batch.status === "READY" || batch.status === "RESERVED") && !!s3?.verified

  const itemColumns = [
    {
      title: "Recording",
      dataIndex: "recordingId",
      key: "recordingId",
      render: (v: string) => (
        <Space size={4}>
          <span style={{ fontFamily: "monospace" }}>{v}</span>
          <Typography.Text copyable={{ text: v }} />
          <a
            href={replayUrl(v)}
            target="_blank"
            rel="noreferrer"
          >
            ▶ Replay
          </a>
        </Space>
      ),
    },
    { title: "Game", dataIndex: "gameId", key: "gameId" },
    {
      title: "Length",
      dataIndex: "durationSec",
      key: "durationSec",
      render: (v: number | null) => (v == null ? "—" : fmtDuration(v)),
    },
    { title: "Status", dataIndex: "status", key: "status", render: (s: string) => <Tag color="cyan">{s}</Tag> },
    {
      title: "Customer",
      dataIndex: "customerStatus",
      key: "customerStatus",
      render: (s: string | null) => (s ? <Tag color={s === "REJECTED" ? "red" : "green"}>{s}</Tag> : "—"),
    },
    {
      title: "",
      key: "reject",
      render: (_: unknown, r: DeliveryItem) => {
        if (r.status !== "DELIVERED" || r.customerStatus === "REJECTED") return null

        return (
          <Button size="small" danger type="text" onClick={() => setRejectItem({ id: r.id, recordingId: r.recordingId })}>
            Reject
          </Button>
        )
      },
    },
  ]

  return (
    <Space direction="vertical" style={{ width: "100%" }} size={16}>
      <Breadcrumb>
        <Breadcrumb.Item>
          <Link to="/data-delivery/contracts">Contracts</Link>
        </Breadcrumb.Item>
        <Breadcrumb.Item>
          <Link to={`/data-delivery/contracts/${batch.contractId}`}>Contract {batch.contractId.slice(0, 8)}…</Link>
        </Breadcrumb.Item>
        <Breadcrumb.Item>Delivery {batch.id.slice(0, 8)}…</Breadcrumb.Item>
      </Breadcrumb>

      <Card
        title={
          <Space>
            <span>Delivery batch {batch.id.slice(0, 8)}…</span>
            <Tag color={BATCH_COLOR[batch.status]}>{batch.status}</Tag>
          </Space>
        }
      >
        <Descriptions column={3} size="small">
          <Descriptions.Item label="Items delivered">{batch.itemCount}</Descriptions.Item>
          <Descriptions.Item label="Delivery hours">{fmtHours(totalSeconds)}</Descriptions.Item>
          <Descriptions.Item label="Delivered to (customer bucket)" span={3}>
            <span style={{ fontFamily: "monospace", fontSize: 12 }}>
              {batch.s3Bucket ? `${batch.s3Bucket}/${batch.deliveryPrefix ?? ""}` : "—"}
            </span>
          </Descriptions.Item>
          <Descriptions.Item label="Manifest">
            <span style={{ fontFamily: "monospace", fontSize: 12 }}>{batch.manifestS3Key ?? "—"}</span>
          </Descriptions.Item>
          <Descriptions.Item label="Sessions manifest">
            <span style={{ fontFamily: "monospace", fontSize: 12 }}>{batch.sessionsS3Key ?? "—"}</span>
          </Descriptions.Item>
          <Descriptions.Item label="Delivered at">
            {batch.deliveredAt ? new Date(batch.deliveredAt).toLocaleString() : "—"}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      <Row gutter={16}>
        <Col flex="auto">
          <Card size="small" title={`Recordings · ${itemCount}`}>
            <Table
              rowKey="id"
              size="small"
              columns={itemColumns}
              dataSource={items}
              pagination={{
                current: itemPage,
                pageSize: itemPageSize,
                total: itemCount,
                showSizeChanger: true,
                pageSizeOptions: [20, 50, 100, 200],
                onChange: (page, size) => {
                  setItemPage(page)
                  setItemPageSize(size)
                },
              }}
            />
          </Card>
        </Col>
        <Col style={{ width: 320 }}>
          <Card size="small" title="Customer bucket verification">
            <Descriptions column={1} size="small">
              <Descriptions.Item label="Status in customer bucket">
                {s3 ? (
                  s3.verified ? <Tag color="green">VERIFIED ✓</Tag> : <Tag color="red">NOT VERIFIED</Tag>
                ) : (
                  <Tag>not built yet</Tag>
                )}
              </Descriptions.Item>
              <Descriptions.Item label="Manifest">{s3?.manifestPresent ? "✓ present" : "—"}</Descriptions.Item>
              <Descriptions.Item label="Receipt">{s3?.receiptPresent ? "✓ present" : "—"}</Descriptions.Item>
              <Descriptions.Item label="Content delivered">
                {s3 ? `${s3.recordingCount} recordings · ${fmtBytes(s3.totalBytes)}` : "—"}
              </Descriptions.Item>
            </Descriptions>
            {s3 && !s3.objectsCopied && !s3.error && (
              <div style={{ color: "#00000073", fontSize: 12, marginTop: 4 }}>
                Object copy is disabled in this environment — manifest + receipt in the customer bucket confirm the delivery
                landed.
              </div>
            )}
            {s3?.error && <div style={{ color: "#cf1322", fontSize: 12, marginTop: 4 }}>Bucket check failed: {s3.error}</div>}
            <Space direction="vertical" style={{ width: "100%", marginTop: 8 }}>
              <Button block type="primary" disabled={!canDeliver} onClick={() => store.markDelivered(batch.id)}>
                Mark delivered
              </Button>
              {!s3?.verified && batch.status !== "DELIVERED" && (
                <div style={{ color: "#00000073", fontSize: 12 }}>
                  Delivery can be confirmed only once the content is verified in the customer bucket.
                </div>
              )}
              {/* A finalize that dropped its connection leaves the batch FAILED
                  with its cuts already staged; retry resumes copy + manifest
                  rather than re-cutting. */}
              <Button
                block
                disabled={batch.status !== "FAILED" && batch.status !== "RESERVED"}
                onClick={() => store.retryBatch(batch.id)}
              >
                Retry build
              </Button>
              <Button
                block
                disabled={batch.status === "CANCELLED"}
                onClick={() => store.watermarkBatch(batch.id)}
              >
                Re-inject watermark jobs
              </Button>
              <Button
                block
                disabled={batch.status === "DELIVERED" || batch.status === "DISPUTED" || batch.status === "CANCELLED"}
                onClick={() => store.cancel(batch.id)}
              >
                Cancel
              </Button>
              <div style={{ color: "#00000073", fontSize: 12 }}>
                Use the <b>Reject</b> action on a row to flag a single delivered video. Rejection is recorded on the delivered
                side only — the source recording and the contract balance are untouched.
              </div>
            </Space>
          </Card>
        </Col>
      </Row>

      <BatchValidation batchId={batch.id} />

      <Modal
        title={rejectItem ? `Reject video ${rejectItem.recordingId.slice(0, 8)}…` : "Reject video"}
        open={!!rejectItem}
        okText="Report rejection"
        okButtonProps={{ danger: true }}
        onCancel={() => {
          setRejectItem(null)
          setReason("")
        }}
        onOk={async() => {
          if (rejectItem) await store.reportRejection(batch.id, reason, rejectItem.id)
          setRejectItem(null)
          setReason("")
        }}
      >
        <Input.TextArea rows={3} value={reason} onChange={e => setReason(e.target.value)} placeholder="Reason for rejection" />
      </Modal>
    </Space>
  )
})
